import RemindAPI from 'lib/RemindAPI';
import PUT_MESSAGE_MUTATION from 'chat/queries/PutMessageMutation.gql';
import {
  putMessage as putMessageDataType,
  putMessageVariables,
  putMessage_putMessage_messages_announcements_item_MessageItem as PutMessageItem,
} from 'chat/queries/__generated__/putMessage';
import SCHEDULE_MESSAGE_MUTATION from 'chat/queries/ScheduleMessageMutation.gql';
import {
  scheduleMessage as scheduleMessageDataType,
  scheduleMessageVariables,
  scheduleMessage_scheduleMessage_messages_announcements_item_MessageItem as ScheduleMessageItem,
} from 'chat/queries/__generated__/scheduleMessage';
import UPDATE_SCHEDULED_MESSAGE_MUTATION from 'chat/queries/UpdateScheduledMessageMutation.gql';
import {
  updateScheduledMessage as updateScheduledMessageDataType,
  updateScheduledMessageVariables,
  updateScheduledMessage_updateScheduledMessage_messages_announcements_item_MessageItem as UpdateScheduleMessageItem,
} from 'chat/queries/__generated__/updateScheduledMessage';
import { cloneDeep } from 'lodash';
import { convertObjectKeysToSnakeCase } from 'utils/camelCase';
import { dispatcher } from 'lib/Dispatcher';
import Message from 'entities/Message';
import { ActionTypes } from 'groups/constants/GroupConstants';
// @ts-ignore
import { addAnnouncementStreamSequenceItem } from 'chat_v2/components/AnnouncementStream';
// @ts-ignore
import { addStreamSequenceItem, broadcastStreamSequenceItem } from 'chat_v2/components/ChatStream';
import ChatActions from 'chat_v2/actions/ChatActions';
import ToastActions from 'actions/ToastActions';

function GQLMessageItemToMessageJSON(
  messageItem: PutMessageItem | ScheduleMessageItem | UpdateScheduleMessageItem,
  seqNumber: string
) {
  let message = cloneDeep(messageItem);
  const groupId = message.group ? message.group.id : 123456789;
  delete message['group'];

  const threads = message.threads.map(thread => ({
    ...thread,
    group_id: groupId,
  }));

  // TODO: refactor this to not use mutability so that the return type of
  // GQLMessageItemToMessageJSON is inferred correctly by TS
  message.linkPreviews.forEach(preview => {
    if (preview.imageUrl) {
      Object.assign(preview, { image: preview.imageUrl });
      delete preview.imageUrl;
    }
  });

  // TODO: refactor this to not use mutability so that the return type of
  // GQLMessageItemToMessageJSON is inferred correctly by TS
  if (message && message.files && message.files.length > 0) {
    message.files.map(file => {
      Object.assign(file, { id: file.uuid });
      return convertObjectKeysToSnakeCase(file, ['contentType', 'previewWidth', 'previewHeight']);
    });
  }

  return {
    ...message,
    id: message.uuid,
    group_ids: [groupId],
    seq: seqNumber,
    send_at: message.sendAt,
    sent_at: message.sentAt,
    can_react: message.canReact,
    created_at: message.createdAt,
    public_url: message.publicUrl,
    reaction_summary: message.reactionSummary,
    link_previews: message.linkPreviews,
    threads,
  };
}

export function putMessage(variables: putMessageVariables) {
  return RemindAPI.execute<putMessageDataType>({
    mutation: PUT_MESSAGE_MUTATION,
    variables,
  }).then(({ putMessage }) => {
    if (putMessage.error) {
      throw new Error(putMessage.error.message);
    }

    putMessage.messages?.announcements.forEach(announcement => {
      const { item } = announcement;
      if (item.__typename !== 'MessageItem') {
        return;
      }

      let data = cloneDeep(item);
      data = GQLMessageItemToMessageJSON(data, announcement.seq);
      const message = Message.fromJSON(data);
      dispatcher.dispatch({
        type: ActionTypes.SEND_GROUP_MESSAGE,
        message,
      });

      try {
        if (item.sender.__typename === 'EntitySender') {
          // the stream UUID is the sender UUID for items sent by an entity
          const streamUUID = item.sender.uuid;
          addAnnouncementStreamSequenceItem(
            streamUUID,
            {
              key: `${item.uuid}.${announcement.seq}`,
              __typename: 'SequenceItem',
              item: item,
            },
            { overrideSequenceNumber: true }
          );
        } else {
          item.threads.forEach(thread => {
            const streamUUID = thread.uuid;
            addAnnouncementStreamSequenceItem(
              streamUUID,
              {
                key: `${item.uuid}.${announcement.seq}`,
                __typename: 'SequenceItem',
                item: item,
              },
              { overrideSequenceNumber: true }
            );
          });
        }
      } catch (e) {
        console.debug(e);
        console.error('could not broadcast message into announcement streams');
      }

      if (item.sender.__typename !== 'EntitySender') {
        try {
          // add the announcement into all the chat stream that
          // are connected to the same group as the announcement
          // if it is not sent by an entity
          broadcastStreamSequenceItem(
            item.group?.uuid,
            item.group?.id,
            {
              key: `${item.uuid}.${announcement.seq}`,
              __typename: 'SequenceItem',
              item: item,
            },
            { overrideSequenceNumber: true }
          );
        } catch (e) {
          console.debug(e);
          console.error('could not broadcast message into chat streams');
        }
      }
    });

    // add the chat messages into all the chat stream.
    putMessage.messages?.chatMessages.forEach(item => {
      try {
        if (item.item.__typename === 'MessageItem') {
          addStreamSequenceItem(item.item.chat?.uuid, item, { overrideSequenceNumber: true });
        }
      } catch (e) {
        console.debug(e);
        console.error('could not add chat message into chat stream');
      }
    });

    // We redirect to an individual chat if we were sending a chat message to one or more individual recipients.
    const needToRedirectToIndividualChat =
      putMessage.messages?.announcements.length === 0 &&
      putMessage.messages?.chatMessages.length >= 1;
    if (
      needToRedirectToIndividualChat &&
      putMessage.messages?.chatMessages[0].item.__typename === 'MessageItem'
    ) {
      const chatUuid = putMessage.messages?.chatMessages[0].item.chat?.uuid;
      chatUuid && ChatActions.selectChat(chatUuid);
    }

    return putMessage;
  });
}

export function scheduleMessage(variables: scheduleMessageVariables) {
  return RemindAPI.execute<scheduleMessageDataType>({
    mutation: SCHEDULE_MESSAGE_MUTATION,
    variables,
  }).then(({ scheduleMessage }) => {
    if (scheduleMessage.error) {
      throw new Error(scheduleMessage.error.message);
    }

    scheduleMessage.messages?.announcements.forEach(announcement => {
      if (announcement.item.__typename !== 'MessageItem') {
        return;
      }

      let item = cloneDeep(announcement.item);
      item = GQLMessageItemToMessageJSON(item, announcement.seq);
      const message = Message.fromJSON(item);
      dispatcher.dispatch({
        type: ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE,
        message,
      });
    });

    return scheduleMessage;
  });
}

export function updateScheduledMessage(variables: updateScheduledMessageVariables) {
  return RemindAPI.execute<updateScheduledMessageDataType>({
    mutation: UPDATE_SCHEDULED_MESSAGE_MUTATION,
    variables,
  }).then(({ updateScheduledMessage }) => {
    if (updateScheduledMessage.error) {
      throw new Error(updateScheduledMessage.error.message);
    }

    updateScheduledMessage.messages?.announcements.forEach(announcement => {
      if (announcement.item.__typename !== 'MessageItem') {
        return;
      }

      let item = cloneDeep(announcement.item);
      item = GQLMessageItemToMessageJSON(item, announcement.seq);
      const message = Message.fromJSON(item);
      dispatcher.dispatch({
        type: ActionTypes.UPDATED_GROUP_SCHEDULED_MESSAGE,
        message,
      });
      ToastActions.showSuccessToast('Announcement updated');
    });

    return updateScheduledMessage;
  });
}

export default { putMessage, scheduleMessage, updateScheduledMessage };
